// ===============================================
// 🌐 БЭКЕНД API СЕРВИС (ФРОНТЕНД)
// ===============================================

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const VISORIA_BACKEND_URL =
  process.env.REACT_APP_VISORIA_BACKEND_URL || BACKEND_URL;

class BackendApiService {
  constructor() {
    this.baseURL = BACKEND_URL;
    this.visoriaBaseURL = VISORIA_BACKEND_URL;
  }

  async makeRequest(endpoint, options = {}, baseURL = this.baseURL) {
    const url = `${baseURL}${endpoint}`;

    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
      },
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);

      if (!response.ok) {
        let message = `HTTP error! status: ${response.status}`;
        let serverError = null;
        let code = null;
        try {
          const errorBody = await response.json();
          if (errorBody?.error) {
            message = errorBody.error;
            serverError = errorBody.error;
          } else if (errorBody?.message) {
            message = errorBody.message;
          }
          if (errorBody?.code) {
            code = errorBody.code;
          }
        } catch {
          // ignore parse errors
        }
        const error = new Error(message);
        error.serverError = serverError;
        error.status = response.status;
        if (code) error.code = code;
        throw error;
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // ===============================================
  // 🎮 STEAM API
  // ===============================================

  // Проверка Steam аккаунта (captchaToken — со 2-го «Подтвердить» с IP)
  async verifySteamAccount(account, captchaToken) {
    return this.makeRequest('/api/steam/verify-account', {
      method: 'POST',
      body: JSON.stringify(
        captchaToken ? { account, captchaToken } : { account },
      ),
    });
  }

  // Создание платежа Steam
  async createSteamPayment(account, amount, currency = 'USD') {
    return this.makeRequest('/api/steam/create-payment', {
      method: 'POST',
      body: JSON.stringify({ account, amount, currency })
    });
  }

  // Пополнение Steam аккаунта
  async topupSteamAccount(account, amount, paymentId) {
    return this.makeRequest('/api/steam/topup-account', {
      method: 'POST',
      body: JSON.stringify({ account, amount, paymentId })
    });
  }

  // ===============================================
  // 💳 СБП API
  // ===============================================

  // Создание QR-кода СБП
  async createSBPQR(payload) {
    return this.makeRequest('/api/sbp/create-qr', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  // Создание криптоплатежа
  async createCryptoPayment(amountRUB, steamAccount, consent = false, captchaToken) {
    return this.makeRequest('/api/sbp/create-crypto-payment', {
      method: 'POST',
      body: JSON.stringify({
        amountRUB,
        steamAccount,
        consent: Boolean(consent),
        ...(captchaToken ? { captchaToken } : {}),
      })
    });
  }

  async getCryptoCommission() {
    return this.makeRequest('/api/currency/crypto-commission', {
      method: 'GET'
    });
  }


  // Проверка статуса платежа СБП
  async checkSBPPaymentStatus(paymentGuid) {
    return this.makeRequest(`/api/sbp/payment-status/${paymentGuid}`, {
      method: 'GET'
    });
  }

  // Новая ссылка на оплату: сервер отвечает {code, error, retryAfter} при отказе
  async postPaymentRetry(url, { headers = {}, credentials } = {}) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      credentials,
      body: '{}',
    });

    let body = {};
    try {
      body = await response.json();
    } catch {
      body = {};
    }

    if (!response.ok || body.success === false) {
      const error = new Error(body.error || `HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.code = body.code || (response.status === 429 ? 'RATE_LIMIT' : undefined);
      error.retryAfter = body.retryAfter;
      throw error;
    }
    return body;
  }

  async checkRetrySteamPayment(orderId) {
    return this.postPaymentRetry(
      `${this.baseURL}/api/sbp/retry-payment/${encodeURIComponent(orderId)}/check`,
    );
  }

  async retrySteamPayment(orderId) {
    return this.postPaymentRetry(
      `${this.baseURL}/api/sbp/retry-payment/${encodeURIComponent(orderId)}`,
    );
  }


  // ===============================================
  // 💱 КУРСЫ ВАЛЮТ API
  // ===============================================

  // Получение курсов валют
  async getCurrencyRates() {
    return this.makeRequest('/api/currency/rates', {
      method: 'GET'
    });
  }

  // Получение информации о валютах
  async getCurrencyInfo() {
    return this.makeRequest('/api/currency/info', {
      method: 'GET'
    });
  }

  // ===============================================
  // 📋 ЗАКАЗЫ
  // ===============================================


  // ===============================================
  // 🏥 HEALTH CHECK
  // ===============================================

  // Проверка здоровья сервера
  async healthCheck() {
    return this.makeRequest('/health', {
      method: 'GET'
    });
  }

  // ===============================================
  // 🎁 VISORIA — другие магазины (через бэкенд)
  // ===============================================

  async getOtherStoreList() {
    return this.makeRequest('/api/visoria', { method: 'GET' }, this.visoriaBaseURL);
  }

  async getOtherStore(storeId) {
    return this.makeRequest(
      `/api/visoria/${encodeURIComponent(storeId)}`,
      { method: 'GET' },
      this.visoriaBaseURL,
    );
  }

  async getOtherStoreUiConfig() {
    return this.makeRequest(
      '/api/visoria/config/ui',
      { method: 'GET' },
      this.visoriaBaseURL,
    );
  }

  async trackReferralVisit(refCode) {
    const url = `${this.visoriaBaseURL}/api/visoria/referral/visit`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refCode }),
    });
    const body = await response.json();
    if (!response.ok || !body.success) {
      throw new Error(body.error || `HTTP error! status: ${response.status}`);
    }
    return body;
  }

  async checkoutOtherStore(storeId, payload) {
    const url = `${this.visoriaBaseURL}/api/visoria/${encodeURIComponent(storeId)}/checkout`;

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    let body = {};
    try {
      body = await response.json();
    } catch {
      body = {};
    }

    if (!response.ok) {
      const error = new Error(body.error || `HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.code = body.code;
      error.amountRub = body.amountRub;
      error.unitRub = body.unitRub;
      error.amountUsdt = body.amountUsdt;
      error.saleUsd = body.saleUsd;
      throw error;
    }

    return body;
  }

  async getOtherStoreOrder(orderId, { accessToken } = {}) {
    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) {
      headers['X-Order-Token'] = accessToken;
    }
    return this.makeRequest(
      `/api/visoria/orders/${encodeURIComponent(orderId)}`,
      { method: 'GET', headers, credentials: 'include' },
      this.visoriaBaseURL,
    );
  }

  async retryOtherStorePayment(orderId, { accessToken, check = false } = {}) {
    const headers = accessToken ? { 'X-Order-Token': accessToken } : {};
    const path = `/api/visoria/orders/${encodeURIComponent(orderId)}/retry-payment`;
    return this.postPaymentRetry(`${this.visoriaBaseURL}${path}${check ? '/check' : ''}`, {
      headers,
      credentials: 'include',
    });
  }

  // ===============================================
  // 👤 ЛИЧНЫЙ КАБИНЕТ (Visoria, вход по коду из письма)
  // ===============================================

  async accountRequest(path, { method = 'GET', body } = {}) {
    const response = await fetch(`${this.visoriaBaseURL}/api/visoria/account${path}`, {
      method,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    let data = {};
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (!response.ok) {
      const error = new Error(data.error || 'Не удалось выполнить запрос. Попробуйте позже.');
      error.status = response.status;
      error.code = data.code;
      error.retryAfter = data.retryAfter;
      throw error;
    }
    return data;
  }

  async requestLoginCode(email, captchaToken) {
    return this.accountRequest('/code', {
      method: 'POST',
      body: captchaToken ? { email, captchaToken } : { email },
    });
  }

  async verifyLoginCode(email, code, consent = false) {
    return this.accountRequest('/verify', {
      method: 'POST',
      body: { email, code, consent: Boolean(consent) },
    });
  }

  /** Без сессии — null, а не ошибка. */
  async getAccount() {
    try {
      const data = await this.accountRequest('/me');
      return data.account ?? null;
    } catch (error) {
      if (error.status === 401) {
        return null;
      }
      throw error;
    }
  }

  async logout() {
    return this.accountRequest('/logout', { method: 'POST', body: {} });
  }

  async logoutAll() {
    return this.accountRequest('/logout-all', { method: 'POST', body: {} });
  }

  async deleteAccount() {
    return this.accountRequest('/delete', { method: 'POST', body: {} });
  }

  async getAccountOrders(page = 1) {
    return this.accountRequest(`/orders?page=${encodeURIComponent(page)}`);
  }

  async getAccountOrder(orderId) {
    const data = await this.accountRequest(`/orders/${encodeURIComponent(orderId)}`);
    return data.order ?? null;
  }

  async getFavorites() {
    const data = await this.accountRequest('/favorites');
    return data.items ?? [];
  }

  async saveFavorite(item) {
    const data = await this.accountRequest('/favorites', { method: 'POST', body: { item } });
    return data.item;
  }

  async removeFavorite(platform, gameKey) {
    return this.accountRequest('/favorites/remove', { method: 'POST', body: { platform, gameKey } });
  }

  async mergeFavorites(items) {
    const data = await this.accountRequest('/favorites/merge', { method: 'POST', body: { items } });
    return data.items ?? [];
  }

  /** Текущие цены избранного; без cookie — запрос не связан с покупателем. */
  async getFavoritePrices(items) {
    const response = await fetch(`${this.visoriaBaseURL}/api/visoria/favorites/prices`, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) {
      throw new Error(`favorite prices HTTP ${response.status}`);
    }
    const data = await response.json();
    return data.prices ?? {};
  }
}

// Создаем единственный экземпляр сервиса
const backendApiService = new BackendApiService();

export default backendApiService;
